import React, { useState, useEffect } from 'react';
import { db, auth } from '../../config/firebase';
import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc, 
  arrayUnion, 
  serverTimestamp, 
  getDocs,
  deleteDoc,
  getDoc 
} from 'firebase/firestore';
import './Reviews.css';

const Reviews = ({ providerId }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [userName, setUserName] = useState('');
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Modified admin check
  useEffect(() => {
    const checkAdmin = async () => {
      if (auth.currentUser?.email === 'admin@aroundu.co.za') {
        setIsAdmin(true);
      }
    };
    checkAdmin();
  }, []);

  // Fetch existing reviews
  useEffect(() => {
    fetchReviews();
  }, [providerId]);

  const fetchReviews = async () => {
    try {
      const reviewsRef = collection(db, 'providers', providerId, 'reviews');
      const reviewsSnapshot = await getDocs(reviewsRef);
      const reviewsList = reviewsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReviews(reviewsList);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!isAdmin) {
      console.log('Not an admin');
      return;
    }
    
    try {
      console.log('Attempting to delete review:', reviewId);
      
      // Delete from reviews subcollection
      const reviewRef = doc(db, 'providers', providerId, 'reviews', reviewId);
      await deleteDoc(reviewRef);
      
      // Recalculate average rating
      const reviewsCollectionRef = collection(db, 'providers', providerId, 'reviews');
      const remainingReviews = await getDocs(reviewsCollectionRef);
      const allReviews = remainingReviews.docs.map(doc => doc.data());
      
      // Calculate new average rating
      const totalRatings = allReviews.reduce((sum, review) => sum + review.rating, 0);
      const averageRating = allReviews.length > 0 ? totalRatings / allReviews.length : 0;

      // Update provider document
      const providerRef = doc(db, 'providers', providerId);
      await updateDoc(providerRef, {
        averageRating: Number(averageRating.toFixed(1)),
        numberOfReviews: allReviews.length,
        lastUpdated: serverTimestamp()
      });
      
      // Refresh reviews list
      await fetchReviews();
      setSuccess('Review deleted successfully');
    } catch (error) {
      console.error('Error deleting review:', error);
      setError('Failed to delete review: ' + error.message);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validate inputs
    if (!userName.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }

    if (rating === 0) {
      setError('Please select a rating');
      setLoading(false);
      return;
    }

    if (!comment.trim()) {
      setError('Please enter a comment');
      setLoading(false);
      return;
    }

    try {
      const reviewData = {
        userName: userName.trim(),
        rating: rating,
        comment: comment.trim(),
        createdAt: new Date().toISOString(),
        providerId: providerId
      };

      // Add to reviews subcollection
      const reviewRef = collection(db, 'providers', providerId, 'reviews');
      await addDoc(reviewRef, reviewData);

      // Update provider stats
      try {
        const reviewsSnapshot = await getDocs(reviewRef);
        const allReviews = reviewsSnapshot.docs.map(doc => doc.data());
        const totalRatings = allReviews.reduce((sum, review) => sum + review.rating, 0);
        const averageRating = totalRatings / allReviews.length;

        const providerRef = doc(db, 'providers', providerId);
        await updateDoc(providerRef, {
          ratings: arrayUnion(rating),
          averageRating: Number(averageRating.toFixed(1)),
          numberOfReviews: allReviews.length,
          lastUpdated: serverTimestamp()
        });
      } catch (updateError) {
        console.error('Error updating provider stats:', updateError);
      }

      // Clear form
      setRating(0);
      setComment('');
      setUserName('');
      
      setSuccess('Thank you for your review!');
      await fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      setError('Failed to submit review. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="reviews-container">
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <form onSubmit={handleReviewSubmit} className="review-form">
        <div className="name-input">
          <label>Your Name:</label>
          <input
            type="text"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            placeholder="Enter your name"
            required
          />
        </div>

        <div className="rating-input">
          <label>Rating:</label>
          <div className="stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <span
                key={star}
                onClick={() => setRating(star)}
                className={star <= rating ? 'star active' : 'star'}
              >
                ★
              </span>
            ))}
          </div>
        </div>

        <div className="comment-input">
          <label>Comment:</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Share your experience..."
            required
          />
        </div>

        <button 
          type="submit" 
          className="submit-button"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Review'}
        </button>
      </form>

      <div className="reviews-list">
        {reviews.map((review) => (
          <div key={review.id} className="review-item">
            <div className="review-header">
              <span className="review-author">{review.userName}</span>
              <span className="review-rating">
                {'★'.repeat(review.rating)}{'☆'.repeat(5-review.rating)}
              </span>
            </div>
            <p className="review-comment">{review.comment}</p>
            <div className="review-footer">
              <span className="review-date">
                {new Date(review.createdAt).toLocaleDateString()}
              </span>
              {isAdmin && (
                <button 
                  onClick={() => handleDeleteReview(review.id)}
                  className="delete-review-button"
                  title="Delete Review"
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Reviews; 